import React from 'react';

import { Accept, FileRejection, useDropzone } from 'react-dropzone';
import { Trans, useTranslation } from 'react-i18next';

import { ReactComponent as Upload } from '../../../assets/icons/upload.svg';
import { EDIT_BTN_NE, TRASH_BTN_NE } from '../../constants/images';
import { IDropzoneValidator } from '../../interfaces/fileUploaderContainer';
import { TRANSLATION_STORIES_CREATE_NEW } from '../../translations/stories';
import { GenericVoidFunction } from '../../types/genericTypes';
import { StoryType, StoryTypeEnum } from '../../types/stories';
import StyledButtonNE from '../StyledButtonNE/StyledButtonNE';
import StyledLinearProgressNE from '../StyledLinearProgressNE/StyledLinearProgressNE';
import StoryPreviewOverlay from './sections/StoryPreviewOverlay/StoryPreviewOverlay';
import './StoryUploader.scss';

interface StoryUploaderProps {
  validator: IDropzoneValidator;
  deleteFile: GenericVoidFunction;
  saveFile: GenericVoidFunction;
  returnError: GenericVoidFunction;
  previewUrl?: string;
  previewMediaType?: StoryType;
  uploading?: number;
  title: string;
}

const StoryUploader = ({
  validator,
  deleteFile,
  saveFile,
  returnError,
  previewUrl,
  previewMediaType,
  uploading
}: StoryUploaderProps) => {
  const { t } = useTranslation();

  const handleDrop = (acceptedFiles: File[], fileRejections: FileRejection[]) => {
    if (acceptedFiles.length > 0) {
      if (previewUrl) deleteFile();
      saveFile(acceptedFiles[0]);
    }
    if (fileRejections.length > 0) {
      returnError();
    }
  };

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop: handleDrop,
    onError: returnError,
    accept: validator.fileFormats as Accept,
    multiple: validator.maxFileCount > 1,
    maxFiles: validator.maxFileCount,
    maxSize: validator.maxFileSize
  });

  return (
    <div className="story-uploader-container" data-experience="new">
      <div className="story-uploader">
        {previewUrl ? (
          <div className="preview">
            <span className="overlay">
              <StoryPreviewOverlay />
            </span>
            {previewMediaType === StoryTypeEnum.video && (
              <video controls muted={true} autoPlay={true} loop={true} role="video">
                <source src={previewUrl} type="video/mp4" />
                {"Sorry, your browser doesn't support embedded videos."}
              </video>
            )}
            {previewMediaType === StoryTypeEnum.image && <img src={previewUrl} role="image" alt="Preview story" />}
          </div>
        ) : uploading !== undefined ? (
          <div className="dropzone uploading">
            <div className="flex flex-column items-center gap-2">
              <p className="ne-font-body-lg font-bold m-0">{t(TRANSLATION_STORIES_CREATE_NEW.form.titleUploading)}</p>
              <p className="ne-font-body-xs m-0">
                <Trans i18nKey={TRANSLATION_STORIES_CREATE_NEW.form.subtitleUploading} components={{ 1: <br /> }} />
              </p>
            </div>
            <div className="w-full px-3">
              <StyledLinearProgressNE value={uploading}></StyledLinearProgressNE>
            </div>
          </div>
        ) : (
          <div {...getRootProps()} className={`dropzone ${isDragActive ? 'active' : ''}`}>
            <input {...getInputProps()} data-testid="dropzone" />
            <Upload />
            <div className="flex flex-column items-center gap-2">
              <p className="ne-font-body-lg font-bold m-0">{t(TRANSLATION_STORIES_CREATE_NEW.form.titleFileInput)}</p>
              <p className="ne-font-body-xs m-0">{t(TRANSLATION_STORIES_CREATE_NEW.form.subtitleFileInput)}</p>
            </div>
            <div className="formats">
              <Trans i18nKey={TRANSLATION_STORIES_CREATE_NEW.form.placeholderFileInput} components={{ 1: <br /> }} />
            </div>
          </div>
        )}
      </div>
      {previewUrl && (
        <div className="action-buttons">
          <StyledButtonNE buttonType="secondary" buttonSize="sm" image={EDIT_BTN_NE} action={open}>
            {t(TRANSLATION_STORIES_CREATE_NEW.form.editButtonPreview)}
          </StyledButtonNE>
          <StyledButtonNE buttonType="secondary" buttonSize="sm" image={TRASH_BTN_NE} action={deleteFile}>
            {t(TRANSLATION_STORIES_CREATE_NEW.form.deleteButtonPreview)}
          </StyledButtonNE>
        </div>
      )}
    </div>
  );
};

export default StoryUploader;
